"use client";

/**
 * Homepage social-proof + discovery surface.
 *
 * Two infinite-marquee rows of recently-analyzed listings. Top row
 * scrolls right-to-left, bottom row scrolls left-to-right (opposite
 * directions reads as energy, not as "everything moving the same way
 * which feels like a single river"). Each row pauses on hover.
 *
 * Card click → /r/<id> - read-only public viewer of the stored
 * analysis. A small "bina.az ↗" button in the corner lets curious
 * visitors jump to the original listing.
 *
 * Renders nothing until the API returns at least one item.
 *
 * Marquee technique: render the row contents twice in the same flex
 * track and animate the track's transform from 0% to -50%. The
 * duplicated half perfectly seams onto the original at the loop
 * point - no JS, no jank.
 */
import { signedPct } from "@/lib/format/change";
import { useEffect, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { getRecentAnalyzed, formatPrice } from "@/lib/api";
import type { RecentAnalyzedItem } from "@/types";
import { trackEvent } from "@/lib/tracking";
import { ExternalLink, MapPin, Home as HomeIcon, Maximize2, ChevronLeft, ChevronRight, Search } from "lucide-react";
import Photo from "@/components/Photo";

function hostnameFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "bina.az";
  }
}

/**
 * Rewrite a bina.azstatic.com `/uploads/full/...` URL to the
 * `/uploads/large/...` variant.
 *
 * `full` serves the original (~900x1200, ~120 KB on this dataset);
 * `large` serves a 315x420 ~20 KB JPEG. Carousel cards display at
 * 260x162 (CSS px), so even on a 3x DPR phone the backing buffer is
 * 780x486 - large is undersized but the slight softness is invisible
 * on a small thumbnail card and the bandwidth saving (16 photos
 * going from ~2 MB total down to ~320 KB) is the right trade for
 * a homepage surface most users only glance at.
 *
 * Non-bina URLs and unrecognised path shapes pass through unchanged
 * so the carousel never blanks out on the off-chance bina renames
 * the variant.
 */
function downscaleBinaUrl(url: string): string {
  if (!url.includes("bina.azstatic.com/uploads/full/")) return url;
  return url.replace("/uploads/full/", "/uploads/large/");
}

function Card({
  item,
  isClone,
  /** "marquee" (default): fixed 260 px width + shrink-0 so the card
   *  fits the horizontal track. "grid": fills the parent grid cell
   *  instead, used by the static homepage row. */
  layout = "marquee",
}: {
  item: RecentAnalyzedItem;
  isClone?: boolean;
  layout?: "marquee" | "grid";
}) {
  const t = useT();

  const viewerHref = `/r/${item.id}`;

  const onCardClick = () => {
    trackEvent("recent_analyzed.click", {
      verdict: item.verdict,
      mode: item.mode,
      via: "card",
      id: item.id,
    });
  };

  // .menzil-marquee-card promotes the card to its own GPU layer so
  // the image inside isn't re-rasterised on each keyframe step of
  // the marquee parent's transform.
  //
  // The marquee renders each card twice so the track can seam onto
  // itself at the loop point. Both copies are visible during the
  // cycle (the LTR row literally starts with the duplicate set on
  // screen), so both have to be clickable - wrapping the duplicate
  // in `inert` killed pointer events on whichever copy happened to
  // be in view, which is the bug users hit on the bottom row. The
  // clone keeps its anchor focusable for the pointer but drops out
  // of the accessibility tree and tab order via `aria-hidden` +
  // `tabIndex={-1}`, so screen readers and keyboard users only
  // encounter each card once.
  //
  // The ENTIRE card is now wrapped in a Link rather than just the
  // image area. Users had no clickability affordance on the text
  // half (price, chips) and were missing the click target. The
  // wrapper Link uses ``group`` so the inner image can zoom and
  // the card itself can lift on hover - clear "this is
  // interactive" signal without changing layout. The source-link
  // button inside the card stops propagation so clicking it still
  // opens the original listing in a new tab instead of navigating
  // to the analysis viewer.
  return (
    <Link
      href={viewerHref}
      onClick={onCardClick}
      tabIndex={isClone ? -1 : undefined}
      aria-hidden={isClone ? true : undefined}
      className={`menzil-marquee-card group bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col transition-[transform,box-shadow,border-color] duration-200 hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
        // Marquee cards take their content's height (the track's
        // items-stretch keeps a row level); the fixed 300 px left a gap
        // under the facts on phones (owner, 2026-10-09).
        layout === "marquee" ? "shrink-0 w-[260px]" : "w-full"
      }`}
    >
      <div className="relative block aspect-[16/10] bg-gray-100 overflow-hidden isolate">
        {/* loading="lazy": carousel runs through 16 photos in two
            duplicated tracks, but only ~2-3 cards fit in the viewport
            at any moment. Native lazy-loading defers the offscreen
            fetches and triggers them as the marquee transform brings
            cards into the viewport - the browser uses the element's
            current bounding box (transforms included) for the
            intersection check, so this works even though the cards
            are moving via translate.

            src is downscaled from /uploads/full/ to /uploads/large/
            on bina.az's CDN (5-6x smaller payload).

            ``group-hover:scale-[1.02]`` adds the secondary
            affordance - the image gently zooms when the card is
            hovered so it reads as "you're pointing at something
            interactive" alongside the card's lift + border colour
            change. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <Photo src={downscaleBinaUrl(item.photo_url)} alt="" className="menzil-marquee-photo transition-transform duration-500 ease-out group-hover:scale-[1.02]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
        {item.mode === "rent" && (
          <span className="absolute top-2 left-2 text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-50/95 text-amber-700 border-amber-200">
            {t("history.badge.rent")}
          </span>
        )}
      </div>

      <div className="p-3.5 flex flex-col flex-1">
        <div className="flex items-baseline gap-2 mb-1">
          {item.price != null && (
            <div className="text-base font-bold text-gray-900">
              {formatPrice(item.price)}{" "}
              <span className="text-[11px] text-gray-500">
                {item.mode === "rent" ? t("result.permonth") : "AZN"}
              </span>
            </div>
          )}
          {item.overpriced_pct != null && (
            <div
              className={`text-xs font-medium ${
                item.overpriced_pct < 0
                  ? "text-emerald-700"
                  : item.overpriced_pct > 5
                    ? "text-rose-600"
                    : "text-gray-500"
              }`}
            >
              {signedPct(item.overpriced_pct, 1)}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-gray-500 mb-3">
          {item.location_name && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {item.location_name}
            </span>
          )}
          {item.room_count != null && (
            <span className="flex items-center gap-1">
              <HomeIcon className="w-3 h-3" />
              {item.room_count} {t("district.rooms.suffix")}
            </span>
          )}
          {item.area_m2 != null && (
            <span className="flex items-center gap-1">
              <Maximize2 className="w-3 h-3" />
              {item.area_m2} m²
            </span>
          )}
        </div>

        <div className="mt-auto flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 group-hover:text-gray-900 transition-colors">
            {t("home.recent.view")}
            <svg
              className="w-2.5 h-2.5 transition-transform duration-200 group-hover:translate-x-0.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
          <button
            type="button"
            tabIndex={isClone ? -1 : undefined}
            aria-hidden={isClone ? true : undefined}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              trackEvent("recent_analyzed.click", {
                verdict: item.verdict,
                mode: item.mode,
                via: "source",
                id: item.id,
              });
              window.open(item.listing_url, "_blank", "noopener,noreferrer");
            }}
            className="inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-900 cursor-pointer"
          >
            {hostnameFromUrl(item.listing_url)}
            <ExternalLink className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
    </Link>
  );
}


/**
 * Auto-scrolling recent-analyzed strip.
 *
 * - Renders up to the latest 16 unique listings the backend returns
 *   (`_RESULT_SIZE` on the API side).
 * - Native `overflow-x-auto` + `snap-x snap-mandatory` so touch swipes
 *   and keyboard arrows work without extra plumbing.
 * - Prev / next buttons (visible on hover at sm+, always tappable on
 *   touch via swipe) drive the strip one card at a time.
 * - A 4 s timer auto-advances when the user isn't interacting; hover
 *   and focus pause it so a manual read isn't interrupted.
 */
export default function RecentlyAnalyzedCarouselAuto() {
  const t = useT();
  const [items, setItems] = useState<RecentAnalyzedItem[] | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getRecentAnalyzed()
      .then((data) => { if (!cancelled) setItems(data); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  // Card width + the strip's gap-4 (16 px) - used both by auto-advance
  // and by the prev/next buttons so they always step by exactly one
  // card. Falls back to 276 px (260 card + 16 gap) when the DOM isn't
  // ready yet (shouldn't happen after items load, but cheap fallback).
  const stepWidth = (el: HTMLDivElement): number => {
    const card = el.querySelector<HTMLElement>("[data-recent-card]");
    return (card?.offsetWidth ?? 260) + 16;
  };

  // Measure the width of one "set" of items (distance from card i in
  // copy A to card i in copy B). Returns 0 before the cards have
  // been laid out.
  const measureSetWidth = (el: HTMLDivElement): number => {
    const cards = Array.from(el.querySelectorAll<HTMLElement>("[data-recent-card]"));
    const visibleCount = Math.min(items?.length ?? 0, 16);
    if (cards.length < visibleCount || visibleCount === 0) return 0;
    const first = cards[0];
    const lastOfSet = cards[visibleCount - 1];
    return (lastOfSet.offsetLeft + lastOfSet.offsetWidth) - first.offsetLeft + 16;
  };

  // True while the user is actively dragging / pinching / swiping the
  // carousel. We never snap during interaction - the wrap waits for
  // the swipe + its momentum to finish so it can never interrupt
  // anything the user is doing.
  const interactingRef = useRef(false);

  // Seat scrollLeft at the middle copy (offset = setWidth). With three
  // copies in the track [copy A | copy B | copy C], starting in B
  // gives the user a full setWidth of buffer in either direction:
  // they can swipe right (showing copy A's tail = "previous items")
  // OR swipe left (showing copy C's head = "next items") without
  // ever hitting the track edge, so the "usual infinite carousel"
  // feel works out of the box with no animation tricks.
  //
  // Layout might not be ready on the first RAF (Next image fill, font
  // swap, etc.), so we retry up to 10 frames until the measurement
  // settles - missing the seating is what makes the carousel feel
  // bounded rather than infinite.
  useEffect(() => {
    if (!items || items.length === 0) return;
    const el = scrollRef.current;
    if (!el) return;
    let attempts = 0;
    let raf = 0;
    const trySeat = () => {
      attempts += 1;
      const w = measureSetWidth(el);
      if (w > 0) {
        el.scrollLeft = w;
        return;
      }
      if (attempts < 10) raf = requestAnimationFrame(trySeat);
    };
    raf = requestAnimationFrame(trySeat);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (!items || items.length <= 1 || paused) return;
    const id = window.setInterval(() => {
      const el = scrollRef.current;
      if (!el || document.hidden) return;
      el.scrollBy({ left: stepWidth(el), behavior: "smooth" });
    }, 4000);
    return () => window.clearInterval(id);
  }, [items, paused]);

  // Silent recenter. Whenever the user lands outside the middle copy
  // (B), we teleport them to the matching offset inside B. Because
  // copies A and C are byte-identical clones of B, the visible frame
  // is preserved across the teleport - the user keeps seeing the
  // exact same item under their finger.
  //
  // The teleport itself MUST be instant. We:
  //   (a) leave scroll-behavior at the browser default ("auto") on
  //       the container (see the className above) so direct
  //       ``scrollLeft = X`` assignment doesn't animate, AND
  //   (b) call scrollTo({behavior: "instant"}) for double-safety
  //       against any inherited smooth-scroll style.
  //
  // Guards so we never wrap mid-gesture:
  //   - interactingRef true → user is touching the strip; skip.
  //   - lastScrollLeftRef unchanged across two consecutive ticks →
  //     the scroll is fully settled (no in-flight momentum from a
  //     fling, no in-flight smooth-scroll from auto-advance). Only
  //     then do we recenter.
  //
  // Recenter rule:
  //   if scrollLeft is in copy A (< setWidth) or copy C (>= 2*setWidth),
  //   teleport to (scrollLeft % setWidth) + setWidth so the user
  //   always ends up inside copy B with a full set-width of buffer
  //   in either direction.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !items || items.length === 0) return;
    let timer: number | undefined;
    let lastScrollLeft = -1;
    const recenter = () => {
      if (interactingRef.current) return;
      const w = measureSetWidth(el);
      if (w === 0) return;
      // Two-tick stability check: if scrollLeft moved since the last
      // tick, the scroll isn't settled yet - re-arm and try again.
      if (el.scrollLeft !== lastScrollLeft) {
        lastScrollLeft = el.scrollLeft;
        timer = window.setTimeout(recenter, 120);
        return;
      }
      if (el.scrollLeft < w || el.scrollLeft >= 2 * w) {
        const target = (el.scrollLeft % w) + w;
        if (target !== el.scrollLeft) {
          el.scrollTo({ left: target, behavior: "instant" as ScrollBehavior });
        }
      }
    };
    const onScroll = () => {
      if (timer) window.clearTimeout(timer);
      lastScrollLeft = -1;
      timer = window.setTimeout(recenter, 120);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Pause + reset the 4 s auto-advance the moment the user touches
  // the carousel; resume ~1.2 s after they release so the reader has
  // a beat to look at whatever card they landed on. Also flips
  // interactingRef so the wrap above suppresses while a swipe is
  // active.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let releaseTimer: number | undefined;
    const onInteractStart = () => {
      interactingRef.current = true;
      if (releaseTimer) window.clearTimeout(releaseTimer);
      setPaused(true);
    };
    const onInteractEnd = () => {
      interactingRef.current = false;
      if (releaseTimer) window.clearTimeout(releaseTimer);
      releaseTimer = window.setTimeout(() => setPaused(false), 1200);
    };
    el.addEventListener("pointerdown", onInteractStart);
    el.addEventListener("touchstart", onInteractStart, { passive: true });
    el.addEventListener("pointerup", onInteractEnd);
    el.addEventListener("pointercancel", onInteractEnd);
    el.addEventListener("touchend", onInteractEnd);
    return () => {
      el.removeEventListener("pointerdown", onInteractStart);
      el.removeEventListener("touchstart", onInteractStart);
      el.removeEventListener("pointerup", onInteractEnd);
      el.removeEventListener("pointercancel", onInteractEnd);
      el.removeEventListener("touchend", onInteractEnd);
      if (releaseTimer) window.clearTimeout(releaseTimer);
    };
  }, []);

  const scrollByCard = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    // Manual button click also pauses + resets the auto timer so
    // the next tick doesn't trample the user's intent.
    setPaused(true);
    window.setTimeout(() => setPaused(false), 1200);
    el.scrollBy({ left: dir * stepWidth(el), behavior: "smooth" });
  };

  if (items === null || items.length === 0) return null;
  const visible = items.slice(0, 16);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">
          {t("home.valuations.title")}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-gray-600 leading-relaxed">
          {t("home.valuations.sub")}
        </p>
      </div>

      <div
        className="relative group/carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        <button
          type="button"
          aria-label={t("home.recent.prev")}
          onClick={() => scrollByCard(-1)}
          className="hidden sm:flex absolute left-0 sm:-left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm items-center justify-center text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-[color,opacity,border-color] opacity-0 group-hover/carousel:opacity-100 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div
          ref={scrollRef}
          // NOTE: scroll-behavior is kept at the browser default ("auto",
          // i.e. instant) so the silent-wrap below is truly silent. We
          // pass behavior: "smooth" explicitly on the auto-advance + nav
          // button calls where we DO want animation. A CSS-level
          // ``scroll-behavior: smooth`` (e.g. Tailwind's scroll-smooth)
          // would animate the wrap's direct ``scrollLeft = X``
          // assignment too, which is exactly the visible "jump to the
          // other end" we're working to eliminate.
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-1 pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {/* Track renders the items THREE times for a truly infinite
              feel. The mount effect seats scrollLeft at setWidth (the
              middle copy), so users have a full set-width of buffer
              in either direction before the wrap kicks in - the
              snap-back lands them at the visually identical position
              in the adjacent copy, so the jump is never visible.
              `data-recent-card` is set only on the middle copy: it's
              the one measureSetWidth + the wrap logic reference. The
              outer copies are aria-hidden + tabIndex=-1 inside the
              Card so screen readers + keyboard tab order encounter
              each listing exactly once. */}
          {visible.map((it) => (
            <div
              key={`a-${it.id}`}
              className="snap-start shrink-0"
              aria-hidden="true"
            >
              <Card item={it} layout="marquee" isClone />
            </div>
          ))}
          {visible.map((it) => (
            <div
              key={`b-${it.id}`}
              data-recent-card
              className="snap-start shrink-0"
            >
              <Card item={it} layout="marquee" />
            </div>
          ))}
          {visible.map((it) => (
            <div
              key={`c-${it.id}`}
              className="snap-start shrink-0"
              aria-hidden="true"
            >
              <Card item={it} layout="marquee" isClone />
            </div>
          ))}
        </div>

        <button
          type="button"
          aria-label={t("home.recent.next")}
          onClick={() => scrollByCard(1)}
          className="hidden sm:flex absolute right-0 sm:-right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm items-center justify-center text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-[color,opacity,border-color] opacity-0 group-hover/carousel:opacity-100 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Funnel into the listings browse/search surface. Filled, not
          outlined - this is the section's one conversion CTA and the
          outlined variant disappeared against the marquee rows. It says
          where it goes (owner, 2026-10-31): it read "Xəritədə axtarın"
          while it opened /listings. */}
      <div className="mt-6 sm:mt-8 text-center">
        <Link
          href="/listings"
          onClick={() => trackEvent("home.recent.browse_all", {})}
          className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition-colors"
        >
          <Search className="w-4 h-4" />
          {t("home.audience.cta.listings")}
        </Link>
      </div>
    </section>
  );
}

